import { Check, ListChecks } from "lucide-react";
import { useTran } from "@/hooks";
import { Badge } from "@/components/ui/badge";
import { getFrameworkMeta } from "../../constants/framework-metadata";
import { useAuthStore } from "@/features/auth";
import { resolveLogoUrl } from "@/lib/utils";
import { useEffect } from "react";

const StepSelectFramework = ({
  formData,
  updateFormData,
  frameworks,
  isEditForm,
  usedFrameworks = [],
}) => {
  const { t } = useTran();
  const { isJJSEA, isWingletFW } = useAuthStore();

  const isLocked = (code) => isEditForm && usedFrameworks.includes(code);

  const availableFrameworks = frameworks
    .filter((f) => {
      if (isJJSEA) return f.code === "JJ-Group"; // only show JJ-Group
      return f.code !== "JJ-Group"; // hide JJ-Group for others
    })
    .filter((f) => isWingletFW || f.code !== "Winglet")
    .map((framework) => {
      const meta = getFrameworkMeta(framework.code);
      return {
        id: framework._id,
        code: framework.code,
        shortName: meta.shortName ?? framework.code,
        fullName: framework.name,
        description: meta.description ?? meta.summary ?? "",
        // Custom frameworks have no entry in the static standard-framework
        // metadata (so meta.image is always undefined for them) — use the
        // uploaded logo the /frameworks endpoint resolves instead. Local
        // storage returns a relative /static/... path that needs the API's
        // origin prepended (UI/API run on different dev ports).
        image: resolveLogoUrl(framework.logoUrl) || meta.image,
        isActive: framework.isActive,
      };
    });

  const toggleFramework = (framework) => {
    if (isLocked(framework.code)) return;

    const exists = formData.frameworks.includes(framework.code);

    if (exists) {
      updateFormData(
        "frameworks",
        formData.frameworks.filter((code) => code !== framework.code),
      );
    } else {
      updateFormData("frameworks", [...formData.frameworks, framework.code]);
    }
  };

  const isSelected = (code) => formData.frameworks.includes(code);

  useEffect(() => {
    if (!isJJSEA) return;

    const hasJJGroup = formData.frameworks.includes("JJ-Group");

    if (!hasJJGroup) {
      updateFormData("frameworks", ["JJ-Group"]);
    }
  }, [isJJSEA]);
  return (
    <div className="rounded-radius border border-border bg-card overflow-hidden">
      <div className="px-5 py-4 border-b border-border flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-10 w-10 rounded-radius bg-primary/10 text-primary flex items-center justify-center shrink-0">
            <ListChecks className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <h2 className="font-bold text-primary">{t("Select Frameworks")}</h2>
            <p className="text-sm text-muted-foreground">
              {t(
                "Select one or more reporting frameworks. Each framework has its own set of metrics that will be assigned to your organizations in the next step.",
              )}
            </p>
          </div>
        </div>
        <Badge
          variant="outline"
          className="shrink-0 border-primary/30 bg-primary/10 px-3 py-1 text-primary"
        >
          {formData.frameworks.length} {t("selected")}
        </Badge>
      </div>

      <div className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2.5">
          {availableFrameworks.map((framework) => {
            const selected = isSelected(framework.code);
            const locked = isLocked(framework.code);
            return (
              <button
                key={framework.id}
                type="button"
                onClick={() => toggleFramework(framework)}
                className={`
  relative min-h-[104px] rounded-radius border p-2.5 text-left
  transition-all duration-150
  ${locked ? "cursor-not-allowed" : "cursor-pointer"}
  ${
    selected
      ? "border-primary bg-primary/5 ring-2 ring-primary/15"
      : "border-border bg-muted/20 hover:border-primary/35 hover:bg-card hover:shadow-sm"
  }
`}
              >
                {selected && (
                  <div className="absolute top-2 right-2 h-4 w-4 rounded-full bg-primary flex items-center justify-center">
                    <Check
                      className="h-2.5 w-2.5 text-primary-foreground"
                      strokeWidth={3}
                    />
                  </div>
                )}

                <div className="flex h-full items-center gap-2.5 pr-3">
                  <div className="flex w-14 shrink-0 flex-col items-center gap-1.5">
                    <div className="h-12 w-12 rounded-radius border border-border bg-background p-1.5 flex items-center justify-center">
                      {framework.image ? (
                        <img
                          src={framework.image}
                          alt={`${t(framework.fullName)} logo`}
                          className="max-h-full max-w-full object-contain"
                        />
                      ) : (
                        <span className="text-sm font-bold text-primary">
                          {t(framework.fullName)?.charAt(0)}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="min-w-0">
                    <p className="text-[12px] font-semibold text-foreground leading-snug">
                      {framework.fullName}
                    </p>
                    <p className="mt-1 text-[10px] leading-4 text-muted-foreground">
                      {t(framework.description)}
                    </p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default StepSelectFramework;
